import { memo, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Typography, useTheme, Button } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import { CollapsibleInfo, SvgIcon } from "components";

import { buildRowsTableTitle } from "../makeData";
import UploadRowsTable from "./UploadRowsTable";

const actionButtonSx = {
  height: 38,
  px: 3,
  border: "1px solid",
  borderColor: "grey.300",
  color: "primary.main",
  fontSize: 14,
  fontWeight: 600,
  textTransform: "none",
};

const UploadRowsSection = ({
  selectedStatuses,
  count,
  rows,
  columns,
  hiddenColumns,
  isLoading,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  onExport,
  onDownloadFile,
}) => {
  const theme = useTheme();
  const title = useMemo(
    () => buildRowsTableTitle(selectedStatuses, count),
    [selectedStatuses, count],
  );

  const collapsibleRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    collapsibleRef.current?.handleExpand(true);
  }, []);

  const handleToggleExpand = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    collapsibleRef.current?.handleExpand(next);
  };

  return (
    <Box mb={3}>
      <CollapsibleInfo
        ref={collapsibleRef}
        hideExpandIcon
        headerStyle={{ backgroundColor: theme.palette.grey[100] }}
        summaryStyles={{ width: "100%" }}
        summary={
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                size="small"
                onClick={handleToggleExpand}
                sx={{ p: 0.25 }}
              >
                {isExpanded ? (
                  <KeyboardArrowDownIcon fontSize="small" />
                ) : (
                  <KeyboardArrowRightIcon fontSize="small" />
                )}
              </IconButton>
              <Typography variant="subtitle1" fontWeight={700}>
                {title}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                onClick={onDownloadFile}
                startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                sx={actionButtonSx}
              >
                Download file
              </Button>

              <Button
                onClick={onExport}
                endIcon={
                  <SvgIcon
                    name="export"
                    height={18}
                    width={18}
                    style={{ color: theme.palette.primary.main }}
                  />
                }
                sx={actionButtonSx}
              >
                Export
              </Button>
            </Box>
          </Box>
        }
        detailsStyle={{ padding: 0 }}
        details={
          <UploadRowsTable
            rows={rows}
            columns={columns}
            hiddenColumns={hiddenColumns}
            isLoading={isLoading}
            pagination={pagination}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
          />
        }
      />
    </Box>
  );
};

UploadRowsSection.propTypes = {
  selectedStatuses: PropTypes.arrayOf(PropTypes.string).isRequired,
  count: PropTypes.number,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string }),
  ).isRequired,
  hiddenColumns: PropTypes.instanceOf(Set).isRequired,
  isLoading: PropTypes.bool,
  pagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
  }).isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onDownloadFile: PropTypes.func.isRequired,
};

export default memo(UploadRowsSection);
